import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { storage } from '@uhg-abyss/web/tools/storage';
import React, { useContext, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeStandardSearchEvent } from '../../../../common/AdobeTagging/adobeStandardSearchEvent';
import { CardWrapperDetailsPage } from '../../../../common/CardWrapper';
import {
  AdobeEventMethods,
  Constants,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../../../common/ConstantsRoutes';
import { phoneOnly } from '../../../../common/ConstantsStyles';
import { DetailsPageHeader } from '../../../../common/DetailsPageHeader/DetailsPageHeader';
import { LoadingSpinnerComp } from '../../../../common/Loader';
import { ProviderDetailTabs } from '../../../../common/ProviderDetailTabs/ProviderDetailTabs';
import { getGeoLocationFromStorage } from '../../../../common/PSXHeader/SearchBar/utils';
import { SendFeedback } from '../../../../common/SendFeedback/SendFeedback';
import {
  getSearchLocation,
  internalConvertProviderTypeToAdobeType,
} from '../../../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { PortalContext } from '../../../../context/PortalContext';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useInitializeStore } from '../../../../hooks/useInitializeStore';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useProviderDetails } from '../../../../hooks/useProviderDetails';
import { useProviderSearchTelemetry } from '../../../../hooks/useProviderSearchTelemetry';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { ProviderType } from '../../../../models/ProviderDetails';
import { useAnalyticsStore } from '../../../../store/useAnalyticsStore';
import { AnalyticsStore } from '../../../../store/useAnalyticsStore/analyticsStore';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../../../store/useDetailsStore';
import { DetailsStore } from '../../../../store/useDetailsStore/detailsStore';
import { useSearchStore } from '../../../../store/useSearchStore';
import { SearchState } from '../../../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import {
  getNetworkStatus,
  getSelectedLocationIndex,
} from '../../../../utils/providerDetails.utils';
import {
  getCoverageTypes,
  getCurrentMember,
} from '../../../../utils/user.utils';
import { concatenateStrings, getTelemetryCoverageType } from '../MapView/utils';
import { FacilityBanner } from './FacilityBanner';
import { toShowEmpirePolicyHospitalBanner } from './FacilityDetailsUtil';

export const FacilityDetails = () => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const { navigate } = useRouter();
  const allConfigs = useLagoon('config')();
  const preferencesData = useStore(StoreKeys.RECOMMENDATION_PREFERENCES);
  const featureInclusion = useLagoon('feature-inclusion')();
  const { coverageType, chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );
  const LOCATIONS_TAB_INDEX = 0;

  const [
    enableSendFeedback,
    enableNewFeedbackFormDesignEarlyPayFlag,
    enableNewFeedbackFormDesignFlag,
    isSearchRelevancyEventTrackingEnabled,
    isDisablePcpDetailsForOutOfDirectory,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SUGGEST_AN_EDIT_FORM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_FEEDBACK_FORM_DESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_DETAILS_IF_OUT_OF_DIRECTORY,
  ]);

  const currentMember = getCurrentMember();

  const enableNewFeedbackFormDesign = currentMember?.isIFPEarlyPay
    ? enableNewFeedbackFormDesignEarlyPayFlag
    : enableNewFeedbackFormDesignFlag;

  const { searchTerm, searchFromTypeAhead, searchType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      searchTerm: state.typeaheadSearchStore.searchTerm,
      searchFromTypeAhead: state.typeaheadSearchStore.searchFromTypeAhead,
      searchType: state.typeaheadSearchStore.searchType,
    }))
  );

  const { avatar = 'clinic', showDirection } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      avatar: state.detailsState.avatar,
      showDirection: state.detailsState.showDirection,
    }))
  );

  const { linkName } = useAnalyticsStore(
    useShallow((state: AnalyticsStore) => ({
      linkName: state.analyticsState.linkName,
    }))
  );

  const { providerId, locationId } = useInitializeStore(
    ConstantsRoutes.FACILITY_DETAILS.key
  );

  const { setShowMaximumProviderLimitBanner } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
  }));

  const { data, isLoading } = useProviderDetails({
    providerId,
    providerType: ProviderType.ORGANIZATION,
    locationId,
  });
  const storedLocation = getGeoLocationFromStorage();

  const [navigateToDirections, setNavigateToDirections] = useState(
    Boolean(showDirection)
  );
  const [currentTabTile, setCurrentTabTitle] = useState<string>(
    Constants.PROVIDER_DETAILS.LOCATIONS
  );
  const [hasQueried, setHasQueried] = useState<boolean>(false);
  const [isMapView, setIsMapView] = useState<boolean>(false);
  const { portalData } = useContext(PortalContext);

  useEffect(() => {
    if (
      data?.facilityDetails?.providerDetails?.providerName &&
      searchFromTypeAhead
    ) {
      adobeStandardSearchEvent({
        linkName,
        term: searchTerm,
        numberOfResults: 1,
        method: AdobeEventMethods.Predictive,
        customAttributesBlock: {
          searchLocation: getSearchLocation(storedLocation),
          providerType: internalConvertProviderTypeToAdobeType(
            ProviderTypes.FACILITY
          ),
        },
      });
    }
  }, [
    data?.facilityDetails?.providerDetails?.providerName,
    searchFromTypeAhead,
  ]);

  useEffect(() => {
    if (showDirection) {
      setNavigateToDirections(true);
    }
  }, [showDirection]);

  const facilityDetails = data?.facilityDetails?.providerDetails || {};
  const providerLocations = facilityDetails?.providerLocations || [];
  const { organizationCode } = facilityDetails;
  const { policyNumber } = currentMember;
  const hasMedicalCoverage = getCoverageTypes(currentMember)?.includes(
    ReverseCoverageTypesCodes.MEDICAL
  );
  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );

  const selectedLocationIndex = getSelectedLocationIndex(
    providerLocations,
    selectedId
  );
  const selectedLocation = providerLocations?.[selectedLocationIndex];

  const networkStatus = getNetworkStatus(selectedLocation?.network?.status);

  const [activeTab, setActiveTab] = useState(LOCATIONS_TAB_INDEX);

  useEffect(() => {
    if (navigateToDirections) {
      setActiveTab(LOCATIONS_TAB_INDEX);
    }
  }, [navigateToDirections]);

  useEffect(() => {
    if (isLoading) {
      setHasQueried(true);
    } else if (
      isLoading == false &&
      hasQueried &&
      !providerLocations?.length &&
      isDisablePcpDetailsForOutOfDirectory
    ) {
      navigate(
        `${
          portalData?.portalConfig?.homePage || '/?chipValue=All'
        }#providerNotFound`
      );
    }
  }, [isLoading]);

  const setHandleTabChange = (index) => {
    setShowMaximumProviderLimitBanner(false);
    if (index !== LOCATIONS_TAB_INDEX) {
      setNavigateToDirections(false);
    }
    adobeLinkTrackEvent({
      name: Object.values(Constants.FACILITY_DETAILS.TABS_LIST)[index],
      location: `body:details tabs`,
      type: 'tab',
    });
    setActiveTab(index);
  };

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [currentPageDetails] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CURRENT_PAGE_DETAILS,
    {}
  );

  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();
  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );

  const hitProviderSearchTelemetry = async () => {
    const providerList = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST
    );
    const selectedProviderIndex =
      storage.session.get(
        Constants.STORAGE_KEYS.SESSION.SELECTED_PROVIDER_INDEX
      ) + 1;

    const filteredResults = providerList
      .map((item) => {
        if (!item) return null; // Skip invalid items

        const providerType = item?.providerType?.toLowerCase();
        const address = item?.displayAddress
          ? item?.displayAddress
          : concatenateStrings(
              item?.address?.line?.join('') || '',
              item?.address?.city || '',
              item?.address?.district || '',
              item?.address?.state || '',
              item?.address?.postalCode || ''
            );

        return {
          resultId: item?.providerId || '',
          resultType:
            providerType === ProviderTypes?.PRACTITIONER
              ? 'PROVIDER'
              : providerType === ProviderTypes.MEDICAL_GROUP
              ? 'GROUP'
              : 'FACILITY',
          name: item?.providerName || item?.displayName || '',
          specialty: item?.speciality?.[0]?.category || item?.speciality || '',
          address,
          coverageType: Array.isArray(coverageType)
            ? coverageType?.map(getTelemetryCoverageType)
            : getTelemetryCoverageType(coverageType),
          rating: item?.healthGradeRating
            ? parseFloat(item?.healthGradeRating).toFixed(1)
            : 0.0,
          numRatings: item?.surveyCount || 0,
          inNetworkLabel: item?.networkStatus || '',
          costIndicationLabel: item?.costIndicator || '',
        };
      })
      .filter(Boolean);
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      timestamp: new Date().toISOString(),
      searchPlatform: 'web',
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius ?? 0,
      },
      interpretedAsNameSearch: searchType !== 'speciality',
      viewType: 'other',
      results: filteredResults,
      resultPage: currentPageDetails?.pageNumber || 1,
      pageSize: providerList?.length,
      resultedInNullSearch: filteredResults?.length === 0,
      selectedResult: {
        resultId: facilityDetails?.providerId,
        resultType:
          facilityDetails?.providerType?.toLowerCase() ===
          ProviderTypes?.PRACTITIONER
            ? 'PROVIDER'
            : facilityDetails?.providerType?.toLowerCase() ===
              ProviderTypes.MEDICAL_GROUP
            ? 'GROUP'
            : 'FACILITY',
        name: facilityDetails?.providerName,
        specialty:
          Array.isArray(providerList[selectedProviderIndex - 1]?.speciality) &&
          providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            ? providerList[selectedProviderIndex - 1]?.speciality[0]?.category
            : providerList[selectedProviderIndex - 1]?.speciality || '',
        address: concatenateStrings(
          facilityDetails?.providerLocations[0]?.address?.line?.join(''),
          facilityDetails?.providerLocations[0]?.address?.city,
          facilityDetails?.providerLocations[0]?.address?.district,
          facilityDetails?.providerLocations[0]?.address?.state,
          facilityDetails?.providerLocations[0]?.address?.postalCode
        ),
        coverageType: Array.isArray(coverageType)
          ? coverageType?.map(getTelemetryCoverageType)
          : getTelemetryCoverageType(coverageType),
        rating:
          facilityDetails?.healthGradeRating &&
          parseFloat(facilityDetails?.healthGradeRating),
        numRatings: facilityDetails?.surveyCount,
        inNetworkLabel: providerList[selectedProviderIndex - 1]?.networkStatus,
        costIndicationLabel:
          facilityDetails?.providerLocations[0]?.costIndicator,
      },
      selectedResultOverallRank: selectedProviderIndex
        ? currentPageDetails?.pageNumber > 1
          ? (currentPageDetails?.pageNumber - 1) *
              currentPageDetails?.pageSize +
            selectedProviderIndex
          : selectedProviderIndex
        : 1,
      selectedResultOnCurrentPageRank: selectedProviderIndex,
      event: 'detail',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      facilityDetails?.providerName &&
      !isLoading &&
      searchTerm &&
      searchTerm?.length > 0 &&
      isSearchRelevancyEventTrackingEnabled
    ) {
      hitProviderSearchTelemetry();
    }
  }, [facilityDetails?.providerName, isLoading, searchTerm]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingSpinnerComp isLoading={isLoading} />
      ) : (
        <React.Fragment>
          {currentTabTile === Constants.FACILITY_DETAILS.LOCATIONS &&
          toShowEmpirePolicyHospitalBanner({
            policyNumber,
            hasMedicalCoverage,
            allConfigs,
            featureInclusion,
            organizationCode,
          }) ? (
            <FacilityBanner />
          ) : null}
          {!(mobileScreen && isMapView) ? (
            <CardWrapperDetailsPage backgroundColor="#FBFCFE">
              <DetailsPageHeader
                avatar={avatar}
                coverageType={coverageType}
                enableNewFeedbackFormDesign={enableNewFeedbackFormDesign}
                enableSendFeedback={enableSendFeedback}
                feedbackFormType={
                  Constants.FEEDBACK_FORM_TYPES.FACILITY_DETAILS
                }
                formProviderType={facilityDetails?.providerType}
                handleTabChange={setHandleTabChange}
                imageLocation={facilityDetails?.imageLocation}
                inNetwork={networkStatus}
                networkId={facilityDetails?.networkId}
                npi={facilityDetails?.npi}
                primarySpeciality={facilityDetails?.organizationType}
                professionalDesignation={undefined}
                providerDetails={facilityDetails}
                providerId={providerId}
                providerLocations={providerLocations}
                providerName={facilityDetails?.providerName}
                providerType={ProviderType.ORGANIZATION}
                setNavigateToDirections={setNavigateToDirections}
              />
            </CardWrapperDetailsPage>
          ) : null}
          <CardWrapperDetailsPage>
            <ProviderDetailTabs
              active={activeTab}
              chipValue={chipValue}
              coverageType={coverageType}
              handleTabChange={(activeTab, title) => {
                adobeLinkTrackEvent({
                  name: title.toLowerCase(),
                  location: `body:details tabs`,
                  type: 'tab',
                });
                setCurrentTabTitle(title);
                setHandleTabChange(activeTab);
              }}
              isMapView={isMapView}
              locationKey={selectedLocation?.locationId}
              navigateToDirections={navigateToDirections}
              preferencesData={preferencesData}
              providerDetails={facilityDetails}
              setIsMapView={setIsMapView}
              setNavigateToDirections={setNavigateToDirections}
            />
          </CardWrapperDetailsPage>
        </React.Fragment>
      )}
      {enableSendFeedback && !enableNewFeedbackFormDesign && (
        <SendFeedback
          coverageType={coverageType}
          data={{
            name: facilityDetails?.providerName,
            address: selectedLocation?.address,
            npi: facilityDetails?.npi,
            mpin: facilityDetails?.providerId,
            providerType: facilityDetails?.providerType,
          }}
          type={Constants.FEEDBACK_FORM_TYPES.FACILITY_DETAILS}
        />
      )}
    </React.Fragment>
  );
};
